// src/ui/ErrorBoundary.tsx
import React from 'react';

type Props = {
  /** Ключ для авто-сброса границы при смене маршрута/секции */
  boundaryKey?: unknown;
  /** Готовый React-узел для фолбэка (если нужен простой вариант) */
  fallback?: React.ReactNode;
  /** Рендер-функция фолбэка (получает error и reset) */
  fallbackRender?: (args: { error: Error | null; reset: () => void }) => React.ReactNode;
  /** Коллбек логирования */
  onError?: (error: Error, info: React.ErrorInfo) => void;
  /** Коллбек на ручной reset() */
  onReset?: () => void;
  children: React.ReactNode;
};

type State = { error: Error | null };

export default class ErrorBoundary extends React.Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(err: Error): State {
    return { error: err };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    this.props.onError?.(error, info);
    // eslint-disable-next-line no-console
    console.error('[ErrorBoundary]', error, info);
  }

  componentDidUpdate(prevProps: Readonly<Props>) {
    if (prevProps.boundaryKey !== this.props.boundaryKey && this.state.error) {
      this.reset();
    }
  }

  reset = () => {
    this.setState({ error: null });
    this.props.onReset?.();
  };

  render() {
    const { fallback, fallbackRender, children } = this.props;
    if (this.state.error) {
      if (fallbackRender) return fallbackRender({ error: this.state.error, reset: this.reset });
      if (fallback) return fallback;
      return (
        <div style={{ padding: 24 }}>
          <h2>Что-то пошло не так.</h2>
          <p>Попробуйте обновить страницу или вернуться назад.</p>
          <button onClick={this.reset}>Попробовать снова</button>
        </div>
      );
    }
    return children;
  }
}
